import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'clsx';
import { ChevronDownIcon } from '@fronton/icons-react';
import { DefaultTFuncReturn } from 'i18next';
import { Typography, TypographyHeaderProps, TypographyTextProps } from '../../typography';
import {
  openPanelArrowIcon,
  PanelBody,
  PanelHeader,
  panel,
  panelArrowIcon,
  panelIcon,
  panelTitleWrapper,
} from './panel-styles';

export type PanelProps = {
  children: React.ReactNode;
  className?: string;
  isOpen?: boolean;
  name: string;
  onClick?: (panelName: string, isOpen: boolean) => void;
  renderLeftIcon?: (visible: boolean) => React.ReactNode;
  title: ((isOpen: boolean) => React.ReactNode) | string | DefaultTFuncReturn;
  isQuestion?: boolean;
} & Partial<TypographyHeaderProps | TypographyTextProps> &
  Omit<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'onClick' | 'title'>;

export const Panel: React.FC<PanelProps> = ({
  children,
  className,
  name,
  isOpen = false,
  onClick,
  renderLeftIcon,
  size = 'body_long',
  title,
  variant = 'm',
  isQuestion = true,
}) => {
  const panelBodyRef = useRef<HTMLDivElement | null>(null);
  const [panelBodyHeight, setPanelBodyHeight] = useState(isOpen ? '100%' : '0px');

  const titleProps = useMemo(() => {
    if (size) {
      return {
        size: isOpen ? 'body_accent' : size,
        variant,
      } as TypographyTextProps;
    }

    return { variant } as TypographyHeaderProps;
  }, [isOpen, size, variant]);

  useEffect(() => {
    if (isOpen && !panelBodyRef.current) {
      setPanelBodyHeight('100%');
    }

    if (panelBodyRef.current) {
      setPanelBodyHeight(isOpen ? `${panelBodyRef.current.scrollHeight}px` : '0px');
    }
  }, [isOpen]);

  const handlePanelClick = useCallback(() => {
    if (onClick) onClick(name, isOpen);
  }, [onClick, name, isOpen]);

  const itemProp = isQuestion ? 'name' : undefined;

  return (
    <div
      className={cn(className, panel)}
      itemProp={isQuestion ? 'mainEntity' : undefined}
      itemScope={isQuestion ? true : undefined}
      itemType={isQuestion ? 'https://schema.org/Question' : undefined}>
      <PanelHeader onClick={handlePanelClick}>
        {renderLeftIcon && <div className={panelIcon}>{renderLeftIcon(isOpen)}</div>}
        <div className={panelTitleWrapper}>
          {typeof title === 'function' ? (
            title(isOpen)
          ) : (
            <Typography as="h3" {...titleProps} itemProp={itemProp}>
              {title}
            </Typography>
          )}
          <ChevronDownIcon
            className={cn(panelArrowIcon, { [openPanelArrowIcon]: isOpen })}
            color="text-primary"
            size="l"
            type="outline"
          />
        </div>
      </PanelHeader>
      <PanelBody ref={panelBodyRef} height={panelBodyHeight} isOpen={isOpen}>
        {children}
      </PanelBody>
    </div>
  );
};
